// src/pages/Accounting/Receipts/utils.ts

import type {
  Receipt,
  ReceiptTax,
  ReceiptMatchStatus,
  Currency,
  FilterKey,
} from "./types";
import { RECEIPT_EMAIL_DOMAIN } from "./constants";

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CAD: "$",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "$",
  INR: "₹",
  MXN: "$",
  BRL: "R$",
};

export function formatCurrency(amount: number, currency: Currency = "CAD"): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? "$";
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}

/**
 * Receipts are always expenses; this formats with a minus sign up front.
 */
export function formatExpense(amount: number, currency: Currency = "CAD"): string {
  return `- ${formatCurrency(amount, currency)}`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "2-digit" });
}

export function formatDateLong(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export function formatUploadedAt(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.round((now - d.getTime()) / 60000);
  if (diffMin < 1) return "uploaded just now";
  if (diffMin < 60) return `uploaded ${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `uploaded ${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `uploaded ${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return `uploaded ${d.toLocaleDateString("en-CA", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })}`;
}

export function totalTaxForReceipt(tax: ReceiptTax): number {
  return (tax.gst ?? 0) + (tax.hst ?? 0) + (tax.pst ?? 0) + (tax.other ?? 0);
}

export function formatTaxBreakdown(tax: ReceiptTax, currency: Currency = "CAD"): string {
  const parts: string[] = [];
  if (tax.gst) parts.push(`GST ${formatCurrency(tax.gst, currency)}`);
  if (tax.hst) parts.push(`HST ${formatCurrency(tax.hst, currency)}`);
  if (tax.pst) parts.push(`PST ${formatCurrency(tax.pst, currency)}`);
  if (tax.other) parts.push(`Other tax ${formatCurrency(tax.other, currency)}`);
  return parts.join(" · ");
}

export function countByMatchStatus(
  receipts: Receipt[],
  status: ReceiptMatchStatus
): number {
  return receipts.filter((r) => r.matchStatus === status).length;
}

export function sumReceipts(receipts: Receipt[]): number {
  return receipts.reduce((sum, r) => sum + r.total, 0);
}

/**
 * Build the user's unique forward address for receipts from their account email.
 * "claire@brightcare.com" becomes "claire+receipts@novala.app"
 * so forwarded vendor emails arrive and are parsed automatically.
 */
export function getUserReceiptEmail(userEmail: string): string {
  const localPart = userEmail.split("@")[0] ?? "user";
  return `${localPart}+receipts@${RECEIPT_EMAIL_DOMAIN}`;
}

/**
 * Filter receipts by match status, or return all when filter is "all".
 */
export function filterReceipts(receipts: Receipt[], filter: FilterKey): Receipt[] {
  if (filter === "all") return receipts;
  return receipts.filter((r) => r.matchStatus === filter);
}